import React, { useEffect, useState } from "react";
import { Modal, Form, Input, DatePicker, Select, Row, Col, message } from "antd";
import Button from "atoms/Button";
import styled from "styled-components";
import moment from "moment";
import { CALL_API } from "common/API";
import { useSelector } from "react-redux";
import { getUserInfo } from "redux/selectors";

const { Option } = Select;

// ─── findWeeksInRange — identical helper used in file 3 ──────────────────────
// Produces [{ weekId, bookedStartDate, bookedEndDate }] for each ISO week
// that overlaps the [start, end] range.
function findWeeksInRange(startDate, endDate) {
  if (!startDate || !endDate) return [];
  const weeks = [];
  let current = moment(startDate).startOf("isoWeek");
  const end = moment(endDate);
  while (current.isSameOrBefore(end, "day")) {
    weeks.push({
      weekId: current.format("YYYY") + "W" + current.isoWeek(),
      bookedStartDate: current.startOf("isoWeek").format("YYYY-MM-DD"),
      bookedEndDate: current.endOf("isoWeek").format("YYYY-MM-DD"),
    });
    current = current.add(1, "week").startOf("isoWeek");
  }
  return weeks;
}

const StyledModal = styled(Modal)`
  .ant-modal-header {
    border-bottom: none;
    padding-top: 20px;
    padding-bottom: 30px;
  }
  .ant-modal-title {
    font-weight: bold;
    font-size: 1.2rem;
  }
  .ant-modal-body {
    padding-top: 0px;
  }
  .ant-modal-footer {
    display: none;
  }
  .ant-form-item-label {
    font-weight: 500;
  }
  .ant-picker,
  .ant-input-number {
    width: 100%;
  }
`;

const ModalWrap = styled.div`
  padding: 3%;
  .ant-picker {
    width: 100%;
  }
`;

const roleOptions = [
  { value: "Lead Attorney", label: "Lead Attorney" },
  { value: "Referring Attorney", label: "Referring Attorney" },
  { value: "Associate", label: "Associate" },
  { value: "Paralegal", label: "Paralegal" },
  { value: "Member", label: "Member" },
];

// ── Mirrors file 3's justification list ──────────────────────────────────────
const justificationOptions = [
  "Availability",
  "Expense/Experience",
  "Subject Matter Expert",
  "Cost/Value",
  "Client Experience/History",
  "Technical/Education Qualification",
];

const statusList = [
  { value: "Assigned", label: "Assigned" },
  { value: "Un Assigned", label: "Un Assigned" },
];

/**
 * EditMatterResourceModal
 *
 * Mirrors EditResourceModal (file 3) for the legal/matter domain.
 *
 * Key parity with file 3:
 *  - weekList + previousWeekList computed via findWeeksInRange
 *  - justification field included
 *  - startDate / finishDate validated and working days re-calculated on change
 *  - status field included
 *  - PATCH payload matches resource-plan API expectations
 */
const EditMatterResourceModal = ({
  open,
  onClose,
  selectedResource,
  onUpdate,
  fetchResourceList,
  matterData,
}) => {
  const [form] = Form.useForm();
  const [days, setDays] = useState(0);
  const userInfo = useSelector(getUserInfo);

  // ─── Working-day calculator (same logic as file 3) ──────────────────────
  const calcWorkingDays = (start, end) => {
    if (!start || !end) {
      setDays(0);
      return;
    }
    let count = 0;
    let current = moment(start);
    const endMoment = moment(end);
    while (current.isSameOrBefore(endMoment, "day")) {
      const dow = current.day();
      if (dow !== 0 && dow !== 6) count++;
      current.add(1, "day");
    }
    setDays(count);
  };

  // ─── Populate form when modal opens / selectedResource changes ───────────
  useEffect(() => {
    if (!selectedResource || !open) return;

    // startDate and finishDate come in as moment objects from resourceData
    // (formatted by the parent). Normalise to moment for safety.
    const toMoment = (d) => {
      if (!d) return null;
      if (moment.isMoment(d)) return d.isValid() ? d : null;
      const m = moment(d, ["YYYY-MM-DD", "DD-MM-YYYY", moment.ISO_8601], true);
      return m.isValid() ? m : null;
    };

    const start = toMoment(selectedResource.startDate);
    const end = toMoment(selectedResource.finishDate);

    form.setFieldsValue({
      name: selectedResource.name || "",
      role: selectedResource.role || "",
      mandays: selectedResource.mandays || 0,
      chargeOut: selectedResource.chargeOut || "",
      startDate: start,
      finishDate: end,
      status: selectedResource.status || "Assigned",
      // ── NEW: justification (matches file 3) ──
      justification: selectedResource.raw?.justification || null,
    });

    calcWorkingDays(start, end);
  }, [selectedResource, open]);

  const handleDateChange = () => {
    const start = form.getFieldValue("startDate");
    const end = form.getFieldValue("finishDate");
    calcWorkingDays(start, end);
  };

  // ─── Submit — mirrors file 3's onFinish ──────────────────────────────────
  const onFinish = async (values) => {
    try {
      const startDate = values.startDate;
      const endDate = values.finishDate;

      if (!startDate || !endDate || !startDate.isValid() || !endDate.isValid()) {
        message.error("Invalid date selected");
        return;
      }

      // ── Week list computation (file 3 parity) ──────────────────────────
      const weekList = findWeeksInRange(startDate, endDate);

      // previousWeekList — derived from the original booked dates (same as file 3)
      const prevWeekList = findWeeksInRange(
        selectedResource?.raw?.actualBookedStartDate || startDate,
        selectedResource?.raw?.actualBookedEndDate || endDate
      );
      const previousWeekList = prevWeekList.map((item) => item.weekId);

      const payload = {
        resourceId: selectedResource?.raw?.resourceId,

        employeeId: selectedResource?.raw?.employeeId,
        employeeName: selectedResource?.name,
        userId: selectedResource?.raw?.userId,

        // Matter-specific identifiers
        matterId: matterData?.matterId,
        matterName: matterData?.matterName,
        clientId: selectedResource?.raw?.clientId || matterData?.clientId,

        projectRole: values.role,
        justification: values.justification,        // ── NEW (file 3 parity)

        actualBookedStartDate: startDate.format("YYYY-MM-DD"),
        actualBookedEndDate: endDate.format("YYYY-MM-DD"),
        startDate: startDate.format("YYYY-MM-DD"),
        endDate: endDate.format("YYYY-MM-DD"),

        personDay: values.mandays,

        resourceExtRate: parseFloat(
          String(selectedResource?.chargeOut || "0").replace(/[^0-9.]/g, "")
        ),

        status: values.status,

        // ── Week lists (file 3 parity) ──────────────────────────────────
        weekList,
        previousWeekList,

        managingOffice:
          userInfo?.managingoffice?.[0] ||
          userInfo?.managingoffice ||
          "",

        // Subcluster support (mirrors file 3)
        ...(selectedResource?.raw?.subclusterbasecountry && {
          subclusterbasecountry: selectedResource.raw.subclusterbasecountry,
        }),
      };

      const res = await CALL_API("insyts/resource-plan", "PATCH", payload);

      if (res?.code === "SUCCESS" || res?.code === 200) {
        message.success("Resource updated successfully");
        if (typeof fetchResourceList === "function") await fetchResourceList();
        if (typeof onUpdate === "function") onUpdate({ ...selectedResource, ...payload });
        onClose();
      } else {
        console.error("API FAILED RESPONSE:", res);
        throw new Error("API failed");
      }
    } catch (err) {
      console.error("UPDATE ERROR:", err);
      message.error("Update failed. Please try again.");
    }
  };

  return (
    <StyledModal
      title="Edit Resource"
      open={open}
      onCancel={onClose}
      footer={null}
      width={800}
      destroyOnClose
    >
      <ModalWrap>
        <Form form={form} layout="vertical" onFinish={onFinish} colon={false}>
          <Row gutter={24}>
            {/* Resource name — read-only */}
            <Col span={12}>
              <Form.Item label="Resource Name" name="name">
                <Input disabled />
              </Form.Item>
            </Col>

            {/* Role */}
            <Col span={12}>
              <Form.Item
                label="Role"
                name="role"
                rules={[{ required: true, message: "Role is required." }]}
              >
                <Select placeholder="Select role" options={roleOptions} />
              </Form.Item>
            </Col>

            {/* Booked start date */}
            <Col span={12}>
              <Form.Item
                label="Booked Start Date"
                name="startDate"
                rules={[{ required: true, message: "Start date is required." }]}
              >
                <DatePicker
                  format="DD-MM-YYYY"
                  style={{ width: "100%" }}
                  onChange={handleDateChange}
                  disabledDate={(current) => {
                    const end = form.getFieldValue("finishDate");
                    return end ? current > end : false;
                  }}
                />
              </Form.Item>
            </Col>

            {/* Booked finish date */}
            <Col span={12}>
              <Form.Item
                label="Booked Finish Date"
                name="finishDate"
                rules={[{ required: true, message: "Finish date is required." }]}
              >
                <DatePicker
                  format="DD-MM-YYYY"
                  style={{ width: "100%" }}
                  onChange={handleDateChange}
                  disabledDate={(current) => {
                    const start = form.getFieldValue("startDate");
                    return start ? current < start : false;
                  }}
                />
              </Form.Item>
            </Col>

            {/* Working days label */}
            <Col span={24}>
              <span
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: "#5D5F68",
                  display: "block",
                  marginBottom: 16,
                }}
              >
                Total Booked Working Days: {days}
              </span>
            </Col>

            {/* Budget mandays */}
            <Col span={12}>
              <Form.Item
                label="Budget Mandays"
                name="mandays"
                rules={[
                  { required: true, message: "Mandays is required." },
                  {
                    validator: (_, value) => {
                      const num = Number(value);
                      if (!value || isNaN(num))
                        return Promise.reject("Enter a valid number.");
                      if (num <= 0)
                        return Promise.reject("Must be greater than 0.");
                      if (num > days)
                        return Promise.reject(
                          `Must be ≤ ${days} (total working days).`
                        );
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input
                  placeholder="Enter mandays"
                  type="number"
                  min={1}
                  max={days}
                />
              </Form.Item>
            </Col>

            {/* Status */}
            <Col span={12}>
              <Form.Item label="Status" name="status">
                <Select options={statusList} />
              </Form.Item>
            </Col>

            {/* Charge out rate — read-only display */}
            <Col span={12}>
              <Form.Item label="Charge Out / hr" name="chargeOut">
                <Input disabled />
              </Form.Item>
            </Col>

            {/* ── NEW: Justification (mirrors file 3) ──────────────────── */}
            <Col span={12}>
              <Form.Item
                label="Justification"
                name="justification"
                rules={[
                  { required: true, message: "Please select a justification." },
                ]}
              >
                <Select placeholder="Select a justification">
                  {justificationOptions.map((j) => (
                    <Option key={j} value={j}>
                      {j}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 10,
              marginTop: 16,
            }}
          >
            <Button type="primary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit">
              Update
            </Button>
          </div>
        </Form>
      </ModalWrap>
    </StyledModal>
  );
};

export default EditMatterResourceModal;